import type { ActionDefinition } from '@gzl10/nexus-sdk'
import type { Context } from 'hono'
import { getHealthRegistry } from './observability.service.js'
import { getOtelConfig } from './observability.config.js'

/**
 * Action: ready
 * GET /observability/ready - Readiness probe (K8s)
 *
 * Returns 200 if all health checks pass, 503 otherwise.
 * No auth required (external probes).
 */
export const readyAction: ActionDefinition = {
  key: 'ready',
  label: { en: 'Readiness Check', es: 'Verificación de disponibilidad' },
  icon: 'mdi:check-circle-outline',
  scope: 'module',
  method: 'GET',
  skipAuth: true,
  output: {},
  handler: async (_ctx, _input, c?: Context<any>) => {
    const registry = getHealthRegistry()
    const result = await registry.checkAll()
    const httpStatus = result.status === 'ok' ? 200 : 503
    if (c) c.status(httpStatus as 200 | 503)
    return result
  }
}

/**
 * Action: metrics-summary
 * GET /observability/metrics-summary - OTel/runtime metrics summary
 *
 * Returns process metrics (uptime, memory, OTel status).
 * Admin-only.
 */
export const metricsSummaryAction: ActionDefinition = {
  key: 'metrics-summary',
  label: { en: 'Metrics Summary', es: 'Resumen de métricas' },
  icon: 'mdi:chart-bar',
  scope: 'module',
  method: 'GET',
  output: {},
  handler: async (ctx, _input, c?: Context<any>) => {
    const user = c?.get('user') as { id?: string } | undefined
    const ability = c?.get('ability') as { can?: (a: string, s: string) => boolean } | undefined
    if (!user) throw new ctx.core.errors.UnauthorizedError('AUTH_REQUIRED')
    if (!ability?.can?.('manage', 'all')) {
      throw new ctx.core.errors.ForbiddenError('Admin access required')
    }

    const config = getOtelConfig()
    const mem = process.memoryUsage()

    return {
      uptime_seconds: Math.round(process.uptime()),
      memory: {
        rss_mb: Math.round(mem.rss / 1024 / 1024),
        heap_used_mb: Math.round(mem.heapUsed / 1024 / 1024),
        heap_total_mb: Math.round(mem.heapTotal / 1024 / 1024)
      },
      otel: {
        enabled: config.enabled,
        service_name: config.serviceName,
        prometheus_port: config.enabled ? config.prometheusPort : null,
        otlp_endpoint: config.enabled ? (config.otlpEndpoint ?? null) : null
      }
    }
  }
}
